'use client';

import React, { createContext, useContext, useEffect, useState } from 'react';
import { logAllPerformanceMetrics, getPerformanceMetrics } from '../utils/performanceLogger';

// Define the context type
type PerformanceContextType = {
  metrics: Record<string, { avg: number; min: number; max: number; count: number }>;
  logMetrics: () => void;
  isEnabled: boolean;
};

// Create the context with default values
const PerformanceContext = createContext<PerformanceContextType>({
  metrics: {},
  logMetrics: () => {},
  isEnabled: false,
});

// Check if performance monitoring is enabled from environment variable
// Use a function to safely check environment variables
const isPerformanceEnabled = () => {
  try {
    return process.env.NEXT_PUBLIC_ENABLE_PERFORMANCE_MONITORING === 'true';
  } catch (e) {
    return false;
  }
};

export const PerformanceProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [metrics, setMetrics] = useState<Record<string, { avg: number; min: number; max: number; count: number }>>({});
  const [isEnabled, setIsEnabled] = useState(false);

  // Initialize isEnabled state after mount
  useEffect(() => {
    setIsEnabled(isPerformanceEnabled());
  }, []);

  // Function to log and update metrics
  const logMetrics = React.useCallback(() => {
    if (!isEnabled) return;

    try {
      const currentMetrics = getPerformanceMetrics();
      //console.log('Current performance metrics:', currentMetrics);
      setMetrics(currentMetrics);

      // Also log to console for debugging
      logAllPerformanceMetrics();
    } catch (error) {
      console.error('Error logging metrics:', error);
    }
  }, [isEnabled]);

  // Log metrics periodically (every 30 seconds)
  useEffect(() => {
    if (!isEnabled) return;

    try {
      // Initial metrics logging
      logMetrics();

      // Set up interval for periodic logging
      const interval = setInterval(() => {
        logMetrics();
      }, 30000); // 30 seconds

      // Clean up interval on unmount
      return () => clearInterval(interval);
    } catch (error) {
      console.error('Error setting up metrics interval:', error);
      return () => {}; // Return empty cleanup function
    }
  }, [logMetrics, isEnabled]);

  // Log metrics when the user leaves the page
  useEffect(() => {
    if (!isEnabled) return;

    try {
      const handleBeforeUnload = () => {
        logMetrics();
      };

      if (typeof window !== 'undefined') {
        window.addEventListener('beforeunload', handleBeforeUnload);

        return () => {
          window.removeEventListener('beforeunload', handleBeforeUnload);
        };
      }
    } catch (error) {
      console.error('Error setting up beforeunload listener:', error);
    }

    return () => {}; // Return empty cleanup function
  }, [logMetrics, isEnabled]);

  return (
    <PerformanceContext.Provider value={{ metrics, logMetrics, isEnabled }}>
      {children}
    </PerformanceContext.Provider>
  );
};

// Custom hook to use the performance context
export const usePerformance = () => useContext(PerformanceContext);

export default PerformanceContext;
